<template>
    <kiosk-layout>
        <div class="vx-flex vx-flex-col vx-gap-12 lg:vx-flex-row">
            <div class="vx-shrink-0 lg:vx-w-60">
                <kiosk-teams-sidebar v-model="fetchParams"></kiosk-teams-sidebar>
            </div>
            <div class="vx-w-full">
                <kiosk-teams-controls
                    v-model="fetchParams"
                    @reset="resetFilters"
                    class="vx-mb-6"
                ></kiosk-teams-controls>
                <page-loading v-if="loading && !list.length"></page-loading>

                <vx-box
                    v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)"
                    color="info"
                    class="vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500"
                >
                    <div class="vx-w-64 vx-mx-auto vx-text-center">
                        No results for current search, try adjusting your filters.
                    </div>
                    <vx-button type="button" color="secondary" @click="resetFilters">
                        Reset All <font-awesome-icon :icon="faCircleX" />
                    </vx-button>
                </vx-box>

                <div v-else-if="list.length">
                    <div class="vx-text-xl vx-font-extrabold">Results</div>
                    <kiosk-teams-table>
                        <kiosk-team-row
                            v-for="(row, index) in response?.data?.data || []"
                            v-model="(response?.data?.data || [])[index]"
                            :key="row.id"
                        ></kiosk-team-row>
                    </kiosk-teams-table>
                </div>

                <div v-else-if="isEqual(defaultFilters(), fetchParams)">
                    <div v-if="favorites.length" class="vx-text-xl vx-font-extrabold">Favorites</div>
                    <kiosk-teams-table v-if="favorites.length">
                        <kiosk-team-row
                            v-for="(row, index) in favorites"
                            v-model="favorites[index]"
                            :key="row.id"
                        ></kiosk-team-row>
                    </kiosk-teams-table>

                    <div class="vx-text-xl vx-font-extrabold">Recents</div>

                    <kiosk-teams-table>
                        <kiosk-team-row
                            v-for="(row, index) in recents"
                            v-model="recents[index]"
                            :key="row.id"
                        ></kiosk-team-row>
                    </kiosk-teams-table>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script setup>
import { faCircleX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTeamRow from '~/components/kiosk/kiosk-teams/KioskTeamRow.vue';
import KioskTeamsControls from '~/components/kiosk/kiosk-teams/KioskTeamsControls.vue';
import KioskTeamsSidebar from '~/components/kiosk/kiosk-teams/KioskTeamsSidebar.vue';
import KioskTeamsTable from '~/components/kiosk/kiosk-teams/KioskTeamsTable.vue';
import { useToasted } from '~/composables/useToasted';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import adminTeamsClient from '~/services/adminTeamsClient';
import { isEqual } from '~/utils/helpers';

const toasted = useToasted();

const defaultFilters = () => ({
    status: undefined,
    parent_id: undefined,
    query: '',
    sort_dir: 'desc',
    sort_field: 'id',
});

const fetchParams = ref(defaultFilters());

const { loading, response, fetchData } = useVersionedRequest();

const list = computed(() => response.value?.data?.data || []);

watch(
    fetchParams,
    () => {
        filterData();
    },
    {
        deep: true,
    }
);

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    filterData();
};

const filterData = () => {
    fetchData(adminTeamsClient.listTeams(fetchParams.value));
};

const favorites = ref([]);
const recents = ref([]);

// on first load favorite teams and recent teams are loaded
const loadInitialData = async () => {
    loading.value = true;

    try {
        const favoritesResponse = await adminTeamsClient.listTeams({ is_favorite: 1 });
        const recentsResponse = await adminTeamsClient.listTeams({
            is_favorite: 0,
            sort_dir: 'desc',
            sort_field: 'id',
        });

        favorites.value = favoritesResponse.data.data;
        recents.value = recentsResponse.data.data;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

loadInitialData();
</script>
